import { Typography, withStyles } from '@material-ui/core';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { fwEndpointBoardCopy as copy, endpoint, tableTranslations } from '../../utils/constants';
import { boardRow, validBoardPayload } from '../../utils/fwEndpointBoard';
import { isCancelled } from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const styles = {
    root: {
        marginBottom: '2rem',
    },
    note: { marginBottom: '0.5rem' },
    table: {
        fontFamily: 'inherit',
        width: '100%',
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '20vh',
    },
};

const escapeHtml = value =>
    String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const statusRenderer = params => {
    const row = params.data ?? {};
    const tooltip = [row.label, row.tooltip].filter(Boolean).join(' — ');
    const title = tooltip ? ` title="${escapeHtml(tooltip)}"` : '';

    return `<span style="color: ${row.color || '#000'}"${title}>${escapeHtml(row.label ?? params.value ?? '')}</span>`;
};

const textRenderer = params => escapeHtml(params.value ?? '');

/**
 * The task's endpoint board from the read model. It owns its request, its
 * cancellation and its failure, so a failed board read never clears the task
 * view it sits in.
 */
const FwEndpointBoard = ({ taskId, classes }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const mounted = useRef(true);
    const source = useRef(null);

    useEffect(() => {
        mounted.current = true;
        const request = cancelToken.source();
        source.current = request;
        setLoading(true);
        setFailed(false);
        setRows([]);
        setTotal(0);
        setTruncated(false);
        axiosInstance
            .get(`${endpoint.FW_READ_MODEL}/tasks/${taskId}/endpoints?limit=200`, {
                cancelToken: request.token,
            })
            .then(response => {
                if (!mounted.current || request !== source.current) return;
                const data = response?.data?.data;
                if (!validBoardPayload(data, taskId)) {
                    setFailed(true);
                    return;
                }
                setRows(data.endpoints.map(boardRow));
                setTotal(
                    Number.isFinite(data.endpoints_total) ? data.endpoints_total : data.endpoints.length
                );
                setTruncated(Boolean(data.truncated));
            })
            .catch(error => {
                if (!mounted.current || request !== source.current || isCancelled(error)) return;
                setFailed(true);
            })
            .finally(() => {
                if (mounted.current && request === source.current) setLoading(false);
            });

        return () => {
            mounted.current = false;
            request.cancel?.();
        };
    }, [taskId]);

    return (
        <div className={classes.root}>
            <Typography variant="h6">{copy.title}</Typography>
            {loading && (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            )}
            {!loading && failed && (
                <Typography className={classes.note} color="error" role="alert">
                    {copy.readError}
                </Typography>
            )}
            {!loading && !failed && truncated && (
                <Typography
                    className={classes.note}>{`${copy.truncated} ${rows.length} z ${total}.`}</Typography>
            )}
            {!loading && !failed && (
                <div className={'ag-theme-alpine'} style={styles.table}>
                    <AgGridReact
                        localeText={tableTranslations}
                        rowData={rows}
                        domLayout="autoHeight"
                        suppressCellSelection
                        pagination
                        paginationPageSize={50}
                        enableCellTextSelection
                        accentedSort>
                        <AgGridColumn
                            field="serial_number"
                            headerName={copy.columns.serial}
                            flex={1}
                            sortable
                            filter
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="label"
                            headerName={copy.columns.status}
                            width={420}
                            sortable
                            filter
                            resizable
                            cellRenderer={statusRenderer}
                        />
                        <AgGridColumn
                            field="signText"
                            headerName={copy.columns.sign}
                            width={120}
                            sortable
                            filter
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="routeText"
                            headerName={copy.columns.route}
                            flex={1}
                            sortable
                            filter
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="sourceText"
                            headerName={copy.columns.source}
                            width={170}
                            sortable
                            filter
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="targetText"
                            headerName={copy.columns.target}
                            width={130}
                            sortable
                            filter
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="pickupText"
                            headerName={copy.columns.pickup}
                            width={150}
                            sortable
                            cellRenderer={textRenderer}
                        />
                        <AgGridColumn
                            field="connectionText"
                            headerName={copy.columns.connection}
                            width={190}
                            sortable
                            cellRenderer={textRenderer}
                        />
                    </AgGridReact>
                </div>
            )}
        </div>
    );
};

export default withStyles(styles)(FwEndpointBoard);
